/**
 * Per-day OB-HOLD overlay (operatingRoomId -> CRNA employeeIds), local to the Board.
 *
 * An OB hospital whose C-section room has nothing scheduled still keeps a CRNA ready for an add-on
 * (Braden 2026-09-16: "even if the c-section rooms are empty, hold 1 CRNA assigned to it"). That room has no cases,
 * so Symphony cannot allocate anyone to it — it returns the hold alongside the allocations (`obHoldCrnas`, keyed by
 * room) and the Board paints the CRNA on that room's plate. Stored per day like the float and relief overlays, so it
 * survives a refresh; never leaves the machine.
 */
export const obHoldKey = (date: string) => `oc-ob-hold-${date}`;

/** operatingRoomId -> the CRNA(s) held there for an add-on C-section. */
export type ObHoldMap = Record<string, string[]>;

export function loadObHold(date: string): ObHoldMap {
  try {
    const raw = localStorage.getItem(obHoldKey(date));
    if (raw) {
      const v = JSON.parse(raw);
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        const out: ObHoldMap = {};
        for (const k of Object.keys(v)) if (Array.isArray(v[k])) out[k] = v[k].filter((x: unknown) => typeof x === 'string');
        return out;
      }
    }
  } catch { /* ignore */ }
  return {};
}

function save(date: string, map: ObHoldMap): ObHoldMap {
  try { localStorage.setItem(obHoldKey(date), JSON.stringify(map)); } catch { /* ignore */ }
  return map;
}

/** Record what Symphony held for one OB room. Replaces that room's entry; other rooms are untouched. */
export function applyObHold(date: string, operatingRoomId: string, crnaIds: string[]): ObHoldMap {
  const map = loadObHold(date);
  if (crnaIds.length) map[operatingRoomId] = [...crnaIds]; else delete map[operatingRoomId];
  return save(date, map);
}

/** Drop every hold — a fresh allocation replaces the day, so the previous run's holds must not linger. */
export function clearObHold(date: string): ObHoldMap {
  try { localStorage.removeItem(obHoldKey(date)); } catch { /* ignore */ }
  return {};
}

/** Everyone currently held for OB — they are "used", so they leave the bullpen. */
export function obHoldEmployeeIds(map: ObHoldMap): Set<string> {
  const ids = new Set<string>();
  for (const arr of Object.values(map)) for (const id of arr) if (id) ids.add(id);
  return ids;
}
